<template>
  <div class="cams-container__camera cams-container__camera--tile">
    <div v-if="grabbableMode" class="grabbable">
      <svg class="icon icon-grabbable">
        <use xlink:href="#icon-grabbable" />
      </svg>
    </div>
    <div class="cams-container__camera-address">
      {{ cameraInfo.address }}
    </div>

    <div
      v-if="cameraInfo.isInactive()"
      class="cams-container__camera-thumb cams-container__camera-thumb--inactive"
      @click="folderMode ? clickOnScreen() : null"
    >
      <div class="cams-container__camera-meta camera-meta">
        <div class="camera-meta__status">
          {{ cameraInfo.inactivityPeriod.reason }}
        </div>
        <div class="camera-meta__todo">
          {{ cameraInfo.inactivityPeriod.todo }}
        </div>
        <div class="camera-meta__number">
          № {{ cameraInfo.number }}
        </div>
      </div>
      <div class="cams-container__camera-fogging" />
    </div>

    <div
      v-else-if="cameraInfo.isDisabled()"
      class="cams-container__camera-thumb cams-container__camera-thumb--inactive"
      @click="folderMode ? clickOnScreen() : null"
    >
      <div class="cams-container__camera-meta camera-meta">
        <div class="camera-meta__status">
          {{ $t('cameraDisabled') }}
        </div>
        <div class="camera-meta__todo">
          {{ cameraInfo.recordDisablePeriod.start | localDateTime }}
        </div>
        <div class="camera-meta__number">
          № {{ cameraInfo.number }}
        </div>
      </div>
      <div class="cams-container__camera-fogging" />
    </div>

    <div
      v-else
      v-observe-visibility="visibilityChanged"
      :class="{'cams-container__camera-thumb--focus': showInfo, 'cams-container__camera-thumb--disabled': isDisabled}"
      class="cams-container__camera-thumb"
      @click="folderMode ? clickOnScreen() : null"
    >
      <template v-if="isVisible && !isDisabled">
        <img
          v-if="cameraInfo.server.screenshot_domain"
          :alt="cameraInfo.title"
          :src="cameraInfo.screenshotUrl(cameraScreenshotSizes.s400, screenshotSign)"
          class="cams-container__video-thumb"
          @error="isDisabled = true"
        >
        <video
          v-else
          :src="cameraInfo.screenshotUrl(cameraScreenshotSizes.s400, screenshotSign)"
          autoplay
          class="cams-container__video-thumb"
          @error="isDisabled = true"
        />
      </template>

      <template v-else>
        <div class="cams-container__camera-meta cams-container__camera-meta--error camera-meta" @click="folderMode ? clickOnScreen() : null">
          <img alt="" src="@/assets/img/icons/video-camera.svg">
          <div class="camera-meta__status">
            {{ $t('cameraNotFound') }}
          </div>
        </div>
      </template>

      <div class="cams-container__camera-meta ">
        <div v-if="typeof showInfo === 'string'">
          <div class="camera-meta__number">
            {{ showInfo }}
          </div>
          <div v-if="selectedAnalyticInfo && !selectedAnalyticInfo.isActive" class="camera-meta__inactive">
            Аналитика не активна
          </div>
        </div>
        <div v-else-if="showInfo === true">
          <div class="camera-meta__tariff">
            {{ cameraInfo.tariffName() }}
          </div>
          <div class="camera-meta__number">
            № {{ cameraInfo.number }}
          </div>
        </div>
        <button v-if="!selectableOnlyMode && cameraInfo.isEmbed && showInfo === true" type="button" @click="showEmbed()">
          {{ $t('embedCamera') }}
          <svg class="icon icon-integration">
            <use xlink:href="#icon-integration" />
          </svg>
        </button>
      </div>

      <div v-show="isSelectedCamera" class="cams-container__selected-camera">
        <div class="cams-container__selected-camera-message">
          <svg class="icon ">
            <use xlink:href="#icon-warning-alarm-screen" />
          </svg>
          <div>{{ $t('cameraSelectedForMonitor') }}</div>
        </div>
      </div>
      <div class="cams-container__camera-fogging" @click="clickOnScreen()" />
    </div>
    <div v-show="isSelectedCameraForFolder" class="cams-container__selected-camera">
      <div class="cams-container__selected-camera-message">
        <svg class="icon">
          <use xlink:href="#icon-select-circle" />
        </svg>
        <div>{{ $t('cameraSelected') }}</div>
      </div>
    </div>
    <div class="cams-container__camera-fogging" @click="clickOnScreen()" />
    <button
      v-if="!selectableOnlyMode && !cameraInfo.isInactive()"
      class="cams-container__camera-controls__btn cams-container__camera-controls__btn--online"
      :title="$t('viewOnline')"
      type="button"
      @click="play()"
    >
      <svg class="icon icon-play">
        <use xlink:href="#icon-play2" />
      </svg>
    </button>
    <button
      v-if="!selectableOnlyMode && !cameraInfo.isInactive() && cameraInfo.isAvailableArchive()"
      class="cams-container__camera-controls__btn cams-container__camera-controls__btn--archive"
      :title="$t('viewArchive')"
      type="button"
      @click="playArchive()"
    >
      <svg class="icon icon-download">
        <use xlink:href="#icon-download" />
      </svg>
    </button>
    <button
      v-if="!cameraInfo.isInactive()"
      class="cams-container__camera-controls__btn cams-container__camera-controls__btn--info"
      :title="$t('infoOfCamera')"
      type="button"
      @click="showInfo = showInfo === true ? false : true"
    >
      <svg class="icon icon-info">
        <use xlink:href="#icon-info2" />
        <title>{{ $t('infoOfCamera') }}</title>
      </svg>
    </button>

    <div class="cams-container__camera-desc">
      <button
        v-for="(analytic, index) in analyticsList"
        :key="analytic.key"
        :class="{
          'cams-container__camera-controls__btn--analytics-inactive': !analytic.isActive,
          'cams-container__camera-controls__btn--analytics-selected': showInfo === analytic.title,
        }"
        class="cams-container__camera-controls__btn--analytics-imager"
        :style="analyticStyle(index)"
        :title="analytic.title"
        type="button"
        @click.stop="showAnalyticInfo(analytic)"
      >
        <svg class="icon icon-analytics">
          <use :xlink:href="`#${analytic.icon}`" />
        </svg>
      </button>
      <EditorCameraTitle
        :camera-info="cameraInfo"
        :is-editable="!selectableOnlyMode"
        class="cams-container__title"
        spinner-color="white"
      />
    </div>
  </div>
</template>

<script>
import {CAMERA_SCREENSHOT_SIZES} from "@/store/cameras/index.js";
import {ANALYTICS} from "@/store/analytics/helpers.js";
import {viewTypeMixin} from "@/components/meshCameras/mixins.js";
import {YM_GOAL_PLAY_VIA_TILE} from "@/utils/tracking.js";

/**
 * Компонент отображает превью информацию по камере в виде плитки.
 *
 * todo разобрать маркеры видимости/отключения
 */
export default {
  name: "ViewTile",
  mixins: [viewTypeMixin],
  data() {
    return {
      showInfo: false,
      isVisible: false,
      isDisabled: false,
      cameraScreenshotSizes: CAMERA_SCREENSHOT_SIZES,
    };
  },
  computed: {
    analyticsList() {
      const result = [];
      const cam = this.cameraInfo;

      if (cam.hasAnalyticThermalVision) {
        result.push({key: ANALYTICS.thermal_vision, icon: 'icon-temperature', title: this.$t('thermalImager')});
      }
      if (cam.hasAnalyticCarNumber) {
        result.push({key: ANALYTICS.car_number, icon: 'icon-car', title: this.$t('carNumberRecognition')});
      }
      if (cam.hasAnalyticFaceRecognition) {
        result.push({key: ANALYTICS.face_recognition, icon: 'icon-face', title: this.$t('faceRecognition')});
      }
      if (cam.hasAnalyticHelmet) {
        result.push({key: ANALYTICS.helmet, icon: 'icon-helmet', title: this.$t('helmetRecognition')});
      }
      if (cam.hasAnalyticMask) {
        result.push({key: ANALYTICS.mask, icon: 'icon-mask', title: this.$t('maskDetector')});
      }
      if (cam.hasAnalyticPerimeter) {
        result.push({key: ANALYTICS.perimeter_security, icon: 'icon-perimeter', title: this.$t('perimeterDetector')});
      }
      if (cam.hasAnalyticCrowd) {
        result.push({key: ANALYTICS.crowd, icon: 'icon-crowd', title: this.$t('crowdDetector')});
      }
      if (cam.hasAnalyticPeopleCount) {
        result.push({key: ANALYTICS.people_counter, icon: 'icon-people-counter', title: this.$t('peopleCounter')});
      }
      if (cam.hasAnalyticMotionAlarm) {
        result.push({key: ANALYTICS.motion_alarm, icon: 'icon-motion', title: this.$t('motionAlarm')});
      }
      if (cam.hasAnalyticParkingCount) {
        result.push({key: ANALYTICS.parking_detection, icon: 'icon-parking', title: this.$t('parkingDetection')});
      }
      if (cam.hasAnalyticFallDetection) {
        result.push({key: ANALYTICS.fall_detection, icon: 'icon-fall', title: 'Падение человека'});
      }
      if (cam.hasAnalyticGarbageAccess) {
        result.push({key: ANALYTICS.garbage_access, icon: 'icon-garbage', title: 'Контроль ТКО'});
      }
      if (cam.hasAnalyticContainerMonitoring) {
        result.push({key: ANALYTICS.container_monitoring, icon: 'icon-garbage-truck', title: 'Транспорт в зоне ТКО'});
      }

      return result
        .map((analytic) => ({
          ...analytic,
          isActive: this.isAnalyticActive(analytic.key),
        }))
        .sort((firstAnalytic, secondAnalytic) => secondAnalytic.isActive - firstAnalytic.isActive);
    },
    selectedAnalyticInfo() {
      if (typeof this.showInfo !== 'string') {
        return null;
      }
      return this.analyticsList.find((analytic) => analytic.title === this.showInfo) || null;
    },
  },
  methods: {
    /**
     * Вычисляет динамические inline-стили для позиционирования кнопки аналитики
     * относительно соседних кнопок в списке analyticsList.
     *
     * Кнопки располагаются горизонтально с перекрытием (эффект стопки карт),
     * где первый (самый активный) элемент находится сверху.
     *
     * @param {number} index элемента аналитики в массиве analyticsList
     * @returns {Object} Объект CSS-стилей со свойствами 'left' (отступ слева) и 'zIndex'
     */
    analyticStyle(index) {
      return {
        left: `${18 + index * 19}px`,
        zIndex: this.analyticsList.length - index,
      };
    },

    /**
     * Проверяет активна ли указанная аналитика для текущей камеры.
     *
     * @param {string} analyticKey - Ключ типа аналитики (например, ANALYTICS.thermal_vision, ANALYTICS.car_number)
     * @returns {boolean} true - если аналитика активна, false - если не активна или отсутствует
     */
    isAnalyticActive(analyticKey) {
      return (this.cameraInfo.activeAnalytics || []).includes(analyticKey);
    },
    /**
     * Переключает отображение информационной панели для выбранной аналитики.
     *
     * При клике на кнопку аналитики показывает информацию о ней вместо стандартной.
     * Повторный клик по той же кнопке скрывает информацию и возвращает стандартный вид.
     *
     * @param {Object} analytic - Объект аналитики, содержащий свойство title (название)
     */
    showAnalyticInfo(analytic) {
      // Если сейчас показана информация об этой аналитике — скрываем (false),
      // иначе показываем информацию о выбранной аналитике (её название)
      this.showInfo = this.showInfo === analytic.title ? false : analytic.title;
    },
    /**
     * Клик по скриншоту вызывает действия в зависимости от режима работы компонента.
     */
    clickOnScreen() {
      this.selectCamera();
      !this.selectableOnlyMode && this.play(YM_GOAL_PLAY_VIA_TILE);
    },
    /**
     * todo
     *
     * @param {Boolean} isVisible
     */
    visibilityChanged(isVisible) {
      this.isVisible = this.isVisible || isVisible;
    },
  },
};
</script>

<style lang="scss" scoped>
.cams-container__selected-camera {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  opacity: 1;
  z-index: 0;
  cursor: pointer;
}

.cams-container__selected-camera-message {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #fff;

  & svg {
    width: 30px;
    height: 30px;
    fill: #fff;
  }

  div {
    text-align: center;
  }
}

.cams-container__camera-desc {
  isolation: isolate;

  .cams-container__camera-controls__btn--analytics-imager {
    position: absolute;
    background-color: #2F84F8;
    border-radius: 50%;

    .icon-analytics {
      background-color: transparent;
    }
  }

  .cams-container__camera-controls__btn--analytics-inactive {
    .icon-analytics {
      border-color: #fff;
      filter: none;
      opacity: 0.35;
    }
  }

  .cams-container__camera-controls__btn--analytics-selected {
    background-color: #fff;
    outline: none;

    .icon-analytics {
      border-color: transparent;
      filter: brightness(0) saturate(100%) invert(45%) sepia(96%) saturate(2417%) hue-rotate(203deg) brightness(100%) contrast(95%);
      opacity: 1;
    }
  }
}
</style>
